import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import python from 'highlight.js/lib/languages/python';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { type FileData, FileType } from '../types';
import LineNumbers from './LineNumbers';

// import '@catppuccin/highlightjs/css/catppuccin-macchiato.css';
import './hl.css';

// Register languages
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('python', python);
hljs.registerLanguage('json', json);
hljs.registerLanguage('xml', xml);

interface CodeViewerProps {
  file: FileData | null;
  replacements?: Record<string, string>;
}

let Container = styled.div`
  height: 100%;
  overflow: auto;
  background-color: black;
  font-family: monospace;
`;

let EmptyState = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

let CodeWrapper = styled.div`
  display: flex;
  min-height: 100%;
`;

let Pre = styled.pre`
  flex: 1;
  overflow-x: auto;
  margin: 0;
`;

let Code = styled.code`
  display: block;
  white-space: pre;

  line-height: 24px;
  font-size: 11px;
`;

let CodeViewer: React.FC<CodeViewerProps> = ({ file, replacements }) => {
  let codeRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (codeRef.current && file?.content) {
      hljs.highlightElement(codeRef.current);
    }
  }, [file]);

  let getLanguage = (fileType: FileType): string => {
    switch (fileType) {
      case FileType.JAVASCRIPT:
        return 'javascript';
      case FileType.TYPESCRIPT:
        return 'typescript';
      case FileType.PYTHON:
        return 'python';
      case FileType.JSON:
        return 'json';
      case FileType.DIRECTORY:
        return 'plaintext';
      case FileType.HTML:
        return 'xml';
      default:
        return 'plaintext';
    }
  };

  if (!file || !file.content) {
    return (
      <EmptyState>
        <p>Select a file to view its contents</p>
      </EmptyState>
    );
  }

  let lineCount = file.content.split('\n').length;

  let code = useMemo(() => {
    if (replacements) {
      return Object.entries(replacements).reduce((content, [key, value]) => {
        return content.replace(new RegExp(key, 'g'), value);
      }, file.content ?? '');
    }

    return file.content ?? '';
  }, [file.content]);

  return (
    <Container>
      <CodeWrapper>
        <LineNumbers count={lineCount} />
        <Pre>
          <Code ref={codeRef} className={`language-${getLanguage(file.type)}`}>
            {code}
          </Code>
        </Pre>
      </CodeWrapper>
    </Container>
  );
};

export default CodeViewer;
